import type {
  ActionType,
  Address,
  Applicant,
  ApplicantEntry,
  ApplicantExpense,
  ApplicantIncome,
  ApplicantTypeEnum,
  AppUser,
  AssetAccount,
  CompanyIncome,
  Financials,
  IncomingProperty,
  LoanApplication,
  OutgoingProperty,
  PeriodicValue,
  PropertyAsset,
  SupportingProperty,
} from '@/types';
import type { BorrowerApplicant, BorrowerGroup, BorrowerRoleEnum } from '@/types/BorrowerGroup';
import {
  ApplicantType,
  AssetType,
  BorrowerType,
  Gender,
  NET_PROFIT_DEFAULT_FREQUENCY,
  RelationshipStatus,
  TrusteeType,
} from './const';
import type { UtmMetricDto } from '@/api/backend/users/applications';
import type { AdditionalFundsRequiredDto } from '@/api/backend/public';
import type { UnaccreditedBrokerProfile } from '@/types/UnaccreditedBrokerProfile';

// Generate a unique ID with a prefix using crypto.randomUUID()
// This guarantees uniqueness without needing to track existing items
export const generateUniqueId = (prefix: string): string => {
  const uuid = crypto.randomUUID().slice(0, 8);
  return `${prefix}-${uuid}`;
};

export const createApplicant = (type: ApplicantTypeEnum = ApplicantType.SECONDARY): ApplicantEntry => {
  if (type === ApplicantType.PRIMARY) {
    return { id: 'applicant-0', type };
  }
  return { id: generateUniqueId('applicant'), type };
};

/**
 * Checks `allowNoContractOfSale` from the URL
 * @returns {boolean}
 */
export const allowNoContractOfSale = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('allowNoContractOfSale') === 'true';
};

// Helper to map applicants to BorrowerApplicant format
export const mapToApplicants = (individuals: ApplicantEntry[], role: BorrowerRoleEnum): BorrowerApplicant[] => {
  return individuals
    .filter((d) => d.firstName && d.lastName)
    .map((d) => ({
      id: `${d.id}-${role}`,
      applicantId: d.id,
      role,
    }));
};

/**
 * Property type labels for the ownership step header
 */
export const PropertyOwnershipTypeLabels: Record<import('@/types/PropertyOwnership').PropertyOwnershipType, string> = {
  incoming: 'Purchase Property',
  outgoing: 'Sale Property',
  supporting: 'Supporting Property',
  asset: 'Asset Property',
} as const;

/**
 * Gets the formatted property header for the borrower ownership UI.
 * Format: "Purchase Property - QLD 4558" (type + state, postcode)
 */
export const getPropertyOwnershipHeader = (
  type: import('@/types/PropertyOwnership').PropertyOwnershipType,
  address?: Address,
): string => {
  const label = PropertyOwnershipTypeLabels[type] ?? 'Property';
  if (!address?.state && !address?.postcode) {
    return label;
  }
  const location = [address?.state, address?.postcode].filter(Boolean).join(' ');
  return location ? `${label} - ${location}` : label;
};

/**
 * Gets the display name for a borrower group based on its type.
 * - Individual: "firstName lastName"
 * - Company: "companyName"
 * - Trust (Individual Trustee): "trustee1 & trustee2 ATF trustName"
 * - Trust (Company Trustee): "companyName ATF trustName"
 */
export const getBorrowerDisplayName = (group: BorrowerGroup, applicants: ApplicantEntry[]): string => {
  if (group.type === BorrowerType.INDIVIDUAL) {
    const applicantId = group.applicants?.[0]?.applicantId;
    const applicant = applicants.find((a) => a.id === applicantId);
    return applicant ? `${applicant.firstName ?? ''} ${applicant.lastName ?? ''}`.trim() : '';
  }

  if (group.type === BorrowerType.COMPANY) {
    return group.companyDetails?.companyName ?? '';
  }

  if (group.type === BorrowerType.TRUST) {
    const trustName = group.trustDetails?.trustName ?? '';

    if (group.trustDetails?.trusteeType === TrusteeType.COMPANY) {
      const companyName = group.trustDetails?.companyDetails?.companyName ?? '';
      return companyName && trustName ? `${companyName} ATF ${trustName}` : companyName || trustName;
    } else {
      // Individual trustees
      const trusteeNames =
        group.applicants
          ?.map((ba) => {
            const applicant = applicants.find((a) => a.id === ba.applicantId);
            return applicant ? `${applicant.firstName ?? ''} ${applicant.lastName ?? ''}`.trim() : '';
          })
          .filter(Boolean)
          .join(' & ') ?? '';
      return trusteeNames && trustName ? `${trusteeNames} ATF ${trustName}` : trusteeNames || trustName;
    }
  }

  return '';
};

export const createFinancials = (): Financials => ({
  income: null,
  expenses: null,
  assets: [],
  liabilities: [],
});

export function createLoanApp(params?: {
  authorisedUser?: Record<string, string>;
  actionType?: ActionType;
  userType?: AppUser;
  brokerId?: string;
}): LoanApplication {
  const newApplicant = createApplicant(ApplicantType.PRIMARY);
  return {
    additionalFundsRequired: {} as AdditionalFundsRequiredDto,
    structuralVersionNumber: parseInt(import.meta.env.VITE_STRUCTURAL_VERSION_NUMBER || '0'),
    applicationId: 'application',
    actionType: params?.actionType ?? 'createNew',
    userType: params?.userType ?? null,
    incomingProperties: [],
    outgoingProperties: [],
    supportingProperties: [],
    applicants: [],
    borrowerGroups: [],
    propertiesOwnership: [],
    financials: { [newApplicant.id]: createFinancials() },
    entityAssets: {},
    endDebt: 0,
    netSurplus: 0,
    verificationCode: '',
    brokerId: params?.brokerId ?? '',
    summaryNotes: '',
    utmMetrics: {} as UtmMetricDto,
    allowNoContractOfSale: allowNoContractOfSale(),
  };
}

export const defaultAdditionalFundRequired = () => ({
  description: '',
  fundsRequiredDate: '',
  fundsRequiredAmount: 0,
  additionalFundsPurposeId: '',
});

export const defaultLoanDetails = () => ({
  savingsAmount: 0,
  giftedAmount: 0,
  reasonDescription: '',
  brokerFeePercentage: 0,
  borrowAmount: 0,
  loanPeriod: undefined,
  loanDate: '',
  calcLoanAmount: 0,
});

export const defaultPeriodicValue = (): PeriodicValue => ({
  amount: 0,
  frequency: 'month',
});

export const defaultAddress = (): Address => ({} as Address);

export const defaultIncomingProperty = (): IncomingProperty => ({
  id: '',
  settlementDate: '',
  propertyType: 'residential',
  address: defaultAddress(),
  propertyPurpose: 'owner_occupied',
  contractForSale: false,
  purchaseAmount: 0,
  depositAmount: 0,
  depositType: 'cash_payment',
  stampDutyStatus: 'already_paid',
  stampDuty: 0,
  securityHeldStatus: 'security_not_held',
  commercialType: '',
});

export const defaultOutgoingProperty = (): OutgoingProperty => ({
  id: '',
  propertyType: 'residential',
  address: defaultAddress(),
  propertyPurpose: 'owner_occupied',
  outstandingLoanAmount: 0,
  saleStatus: 'no',
  settlementDate: '',
  saleAmount: 0,
  depositAmount: 0,
  depositInTrust: false,
  securityHeldStatus: 'security_not_held',
  commercialType: '',
});

export const defaultSupportingProperty = (): SupportingProperty => ({
  id: '',
  propertyType: 'residential',
  address: defaultAddress(),
  propertyPurpose: 'owner_occupied',
  outstandingLoanAmount: 0,
  supportingPropertyValue: 0,
  securityHeldStatus: 'security_not_held',
});

export const defaultApplicant = (): Applicant => ({
  id: '',
  type: ApplicantType.PRIMARY,
  firstName: '',
  middleName: '',
  lastName: '',
  preferredName: '',
  gender: Gender.PREFER_NOT_TO_SAY,
  genderOther: '',
  birthdate: '',
  address: defaultAddress(),
  mobile: '',
  email: '',
  confirmEmail: '',
  numberOfDependants: 0,
  relationshipStatus: RelationshipStatus.SINGLE,
  relationshipToPrimary: undefined,
  relationshipOtherInfo: '',
});

export const defaultUnaccreditedBrokerProfile = (): UnaccreditedBrokerProfile => ({
  id: '',
  firstName: '',
  lastName: '',
  mobile: '',
  email: '',
  aggregator: undefined,
  state: undefined,
});

export const defaultIncome = (): ApplicantIncome => ({
  applicantId: '',
  employment: [],
  shortTermChange: false,
  shortTermEmploymentChange: '',
  centrelinkPayments: defaultPeriodicValue(),
  childSupportMaintenance: defaultPeriodicValue(),
  familyAllowance: defaultPeriodicValue(),
  familyTaxBenefit: defaultPeriodicValue(),
  governmentPension: defaultPeriodicValue(),
  dividendsInvestments: defaultPeriodicValue(),
  superannuation: defaultPeriodicValue(),
  other: defaultPeriodicValue(),
  expectIncomeChange: false,
  effectOnIncomeChange: '',
  incomeExpectation: {},
  employmentDetail: {},
});

export const defaultExpenses = (): ApplicantExpense => ({
  numberOfDependants: 0,
  groceries: defaultPeriodicValue(),
  clothing: defaultPeriodicValue(),
  education: defaultPeriodicValue(),
  insurancePremium: defaultPeriodicValue(),
  childcare: defaultPeriodicValue(),
  medical: defaultPeriodicValue(),
  recreation: defaultPeriodicValue(),
  communication: defaultPeriodicValue(),
  homePropertyCost: defaultPeriodicValue(),
  investmentPropertyCost: defaultPeriodicValue(),
  transport: defaultPeriodicValue(),
  other: defaultPeriodicValue(),
  predictedExpenseChange: defaultPeriodicValue(),
});

export const defaultPropertyAsset = (): PropertyAsset => ({
  id: '',
  type: AssetType.PROPERTY,
  address: defaultAddress(),
  propertyValue: 0,
  propertyPurpose: 'investment_property',
  rentalIncome: defaultPeriodicValue(),
  amountOwing: 0,
  assetNumber: 0,
});

export const defaultAssetAccount = (): AssetAccount => ({
  id: '',
  type: AssetType.SAVINGS,
  institution: '',
  value: 0,
  assetNumber: 0,
});

export const defaultApplicantIncome = (): ApplicantIncome => ({
  applicantId: '',
  employment: [],
  shortTermChange: false,
  shortTermEmploymentChange: '',
  centrelinkPayments: defaultPeriodicValue(),
  childSupportMaintenance: defaultPeriodicValue(),
  familyAllowance: defaultPeriodicValue(),
  familyTaxBenefit: defaultPeriodicValue(),
  governmentPension: defaultPeriodicValue(),
  dividendsInvestments: defaultPeriodicValue(),
  superannuation: defaultPeriodicValue(),
  other: defaultPeriodicValue(),
  expectIncomeChange: false,
  effectOnIncomeChange: '',
  incomeExpectation: {},
  employmentDetail: {},
});

/**
 * Creates default company income object for corporate borrowers (Company/Trust with Company trustee)
 * Used to store net profit for the most recent financial year
 */
export const createCompanyIncome = (borrowerGroupId = ''): CompanyIncome => ({
  netProfit: { amount: 0, frequency: NET_PROFIT_DEFAULT_FREQUENCY },
  borrowerGroupId,
});
